import type { AppMode } from '@iheartjane/types';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { isEmbeddedMode } from '@jane/shared/util';

import { useHeadlessApp } from '../headlessAppProvider/headlessAppProvider';

interface EcommAppContextValues {
  appMode: AppMode;
  isEmbed: boolean;
  isIframe: boolean;
}

const ecommAppContextValue = (appMode: AppMode): EcommAppContextValues => {
  const isIframe = ['embedded', 'operatorEmbed'].includes(appMode);
  const isEmbed = isEmbeddedMode(appMode);
  return { appMode, isEmbed, isIframe };
};

const EcommAppContext = createContext<EcommAppContextValues>(
  ecommAppContextValue('default')
);

export const EcommAppProvider = ({
  appMode,
  children,
}: PropsWithChildren<{ appMode: AppMode }>) => {
  const { options } = useHeadlessApp();
  const derivedAppMode = options?.bloomUserExperience ? 'bloom' : appMode;

  const contextValue = useMemo(() => {
    return ecommAppContextValue(derivedAppMode);
  }, [derivedAppMode]);

  return (
    <EcommAppContext.Provider value={contextValue}>
      {children}
    </EcommAppContext.Provider>
  );
};

export const useSafeEcommApp = () => useContext(EcommAppContext);

export const useEcommApp = () => {
  const context = useContext(EcommAppContext);

  if (!Object.keys(context).length) {
    throw new Error(
      'useEcommApp must be used within an EcommAppProvider. Use useSafeEcommApp if used within a component shared with non-Ecomm apps'
    );
  }

  return context;
};
